import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaArrowLeft, 
  FaGlobe, 
  FaLock, 
  FaUserPlus,
  FaExclamationTriangle,
  FaImage, 
  FaSave,
  FaTrash
} from 'react-icons/fa';
import { supabase } from '../../../data/supabaseClient';
import { AuthContext } from '../../../Provider';
import { useToast } from '../../../hooks/ToastContext';
import Spinner from '../../../common/Spinner';
import { CustomButton2, CustomButton8 } from '../../../Templates';
import '../../styles/GroupsPage.css';

const EditGroupPage = () => {
  const { id: groupId } = useParams();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [group, setGroup] = useState(null);
  const [userRole, setUserRole] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    visibility: 'public',
    category: '',
    location: '',
    schedule: '',
    meetingPoint: '',
    maxMembers: '',
    bannerImage: null,
    error: null
  });
  
  const [imagePreview, setImagePreview] = useState(null);
  
  // Categories options
  const categories = [
    'commuters',
    'sports',
    'nature',
    'tourism',
    'hobby',
    'family',
    'other'
  ];
  
  // Load group data
  useEffect(() => {
    const fetchGroupData = async () => {
      if (!groupId || !user) {
        navigate('/login');
        return;
      }
      
      try {
        setLoading(true);
        
        // Fetch group details
        const { data: groupData, error: groupError } = await supabase
          .from('groups')
          .select('*')
          .eq('id', groupId)
          .single();
        
        if (groupError) throw groupError;
        
        setGroup(groupData);
        
        // Check if user is an admin of the group
        const { data: memberData, error: memberError } = await supabase
          .from('group_members')
          .select('role')
          .eq('group_id', groupId)
          .eq('user_id', user.id)
          .eq('status', 'active')
          .single();
        
        if (memberError) {
          // If not a member/admin, redirect back to the group page
          addToast({
            type: 'error',
            title: t('errors.access_denied'),
            description: t('errors.admin_required')
          });
          navigate(`/groups/${groupId}`);
          return;
        }
        
        setUserRole(memberData.role);
        
        // Only allow admins to edit group details
        if (memberData.role !== 'admin') {
          addToast({
            type: 'error',
            title: t('errors.access_denied'),
            description: t('errors.admin_required')
          });
          navigate(`/groups/${groupId}`);
          return;
        }
        
        // Initialize form with group data
        setFormData({
          name: groupData.name || '',
          description: groupData.description || '',
          visibility: groupData.visibility || 'public',
          category: groupData.category || '',
          location: groupData.location || '',
          schedule: groupData.schedule || '',
          meetingPoint: groupData.meeting_point || '',
          maxMembers: groupData.max_members || '',
          error: null
        });
        
        // Set image preview if exists
        if (groupData.banner_image_url) {
          setImagePreview(groupData.banner_image_url);
        }
        
      } catch (error) {
        console.error('Error fetching group data:', error);
        addToast({
          type: 'error',
          title: t('errors.fetch_error'),
          description: t('errors.group_fetch_failed')
        });
        navigate('/groups');
      } finally {
        setLoading(false);
      }
    };
    
    fetchGroupData();
  }, [groupId, user, navigate, t, addToast]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      error: null // Clear previous error when field is edited
    }));
  };
  
  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setFormData(prev => ({
        ...prev,
        error: t('errors.file_too_large')
      }));
      return;
    }
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setFormData(prev => ({
        ...prev,
        error: t('errors.invalid_file_type')
      }));
      return;
    }
    
    // Create image preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    setFormData(prev => ({
      ...prev,
      bannerImage: file,
      error: null
    }));
  };
  
  // Remove banner image
  const handleRemoveImage = () => {
    setImagePreview(null);
    setFormData(prev => ({
      ...prev,
      bannerImage: 'remove',
      error: null
    }));
  };
  
  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('errors.name_required');
    }
    
    if (!formData.description.trim()) {
      newErrors.description = t('errors.description_required');
    }
    
    if (!formData.category) {
      newErrors.category = t('errors.category_required');
    }
    
    if (formData.maxMembers && (isNaN(formData.maxMembers) || parseInt(formData.maxMembers) < 0)) {
      newErrors.maxMembers = t('errors.invalid_member_count');
    }
    
    if (Object.keys(newErrors).length > 0) {
      setFormData(prev => ({
        ...prev,
        error: Object.values(newErrors)[0]
      }));
      return false;
    }
    
    return true;
  };
  
  // Update group
  const handleUpdateGroup = async () => {
    if (!validateForm()) {
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: formData.error
      });
      return;
    }
    
    setSaving(true);
    
    try {
      let bannerImageUrl = group.banner_image_url;
      
      // Handle banner image
      if (formData.bannerImage) {
        if (formData.bannerImage === 'remove') {
          // Remove existing image
          if (bannerImageUrl) {
            // Extract file path from URL to delete it
            const urlParts = bannerImageUrl.split('/');
            const fileName = urlParts[urlParts.length - 1];
            const filePath = `group_banners/${fileName}`;
            
            // Delete file from storage (ignore errors)
            await supabase.storage
              .from('group')
              .remove([filePath]);
            
            bannerImageUrl = null;
          }
        } else {
          // Upload new image
          const fileExt = formData.bannerImage.name.split('.').pop();
          const fileName = `${groupId}-${Date.now()}.${fileExt}`;
          const filePath = `group_banners/${fileName}`;
          
          const { error: uploadError } = await supabase.storage
            .from('group')
            .upload(filePath, formData.bannerImage, {
              contentType: formData.bannerImage.type,
              cacheControl: '3600',
              upsert: true
            });
          
          if (uploadError) {
            console.error('Image upload error:', uploadError);
            addToast({
              type: 'warning',
              title: t('errors.image_upload_failed'),
              description: t('errors.continuing_without_image')
            });
          } else {
            const { data } = supabase.storage.from('group').getPublicUrl(filePath);
            bannerImageUrl = data.publicUrl;
          }
        }
      }
      
      // Update group
      const { error: groupError } = await supabase
        .from('groups')
        .update({
          name: formData.name.trim(),
          description: formData.description.trim(),
          visibility: formData.visibility,
          category: formData.category,
          location: formData.location.trim() || null,
          banner_image_url: bannerImageUrl,
          schedule: formData.schedule.trim() || null,
          meeting_point: formData.meetingPoint.trim() || null,
          max_members: formData.maxMembers ? parseInt(formData.maxMembers) : null
        })
        .eq('id', groupId);
      
      if (groupError) throw groupError;
      
      addToast({
        type: 'success',
        title: t('success'),
        description: t('groups.group_updated_success')
      });
      
      // Navigate back to group page
      navigate(`/groups/${groupId}`);
      
    } catch (error) {
      console.error('Error updating group:', error);
      addToast({
        type: 'error',
        title: t('errors.update_failed'),
        description: t('errors.group_update_failed')
      });
      setSaving(false);
    }
  };
  
  // Navigate back to group
  const handleBack = () => {
    navigate(`/groups/${groupId}`);
  };
  
  if (loading) {
    return (
      <>
        <div className="groups-page">
          <Spinner size="large" />
        </div>
      </>
    );
  }
  
  if (!group) {
    return (
      <>
        <div className="groups-page">
          <div className="groups-empty">
            <h3>{t('groups.group_not_found')}</h3>
            <CustomButton2
              label={t('groups.back_to_groups')}
              onClick={() => navigate('/groups')}
            />
          </div>
        </div>
      </>
    );
  }
  
  return (
    <>
      <div className="create-group-container">
        <div className="back-button" style={{ marginBottom: '20px' }}>
          <CustomButton8 onClick={handleBack}>
            <FaArrowLeft />
          </CustomButton8>
        </div>
        
        <h2 className="form-heading">{t('groups.edit_group')}</h2>
        <p className="group-context">{t('groups.editing')}: <strong>{group.name}</strong></p>
        
        {formData.error && (
          <div className="error-message">
            <FaExclamationTriangle />
            <span>{formData.error}</span>
          </div>
        )}
        
        <div className="create-group-form">
          <div className="form-group">
            <label htmlFor="name">{t('groups.group_name')} *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('groups.group_name_placeholder')}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="description">{t('groups.group_description')} *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder={t('groups.group_description_placeholder')}
              rows={6}
            />
            <span className="hint">{t('groups.description_hint')}</span>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">{t('groups.category')} *</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
              >
                <option value="">{t('groups.select_category')}</option>
                {categories.map(category => (
                  <option key={category} value={category}>
                    {t(`groups.categories.${category}`)}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="visibility">{t('groups.visibility')}</label>
              <select
                id="visibility"
                name="visibility"
                value={formData.visibility}
                onChange={handleChange}
              >
                <option value="public">
                  {t('groups.public')} - <FaGlobe size={12} />
                </option>
                <option value="private">
                  {t('groups.private')} - <FaLock size={12} />
                </option>
                <option value="invite-only">
                  {t('groups.invite_only')} - <FaUserPlus size={12} />
                </option>
              </select>
              <span className="hint">
                {formData.visibility === 'public' && t('groups.public_hint')}
                {formData.visibility === 'private' && t('groups.private_hint')}
                {formData.visibility === 'invite-only' && t('groups.invite_only_hint')}
              </span>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="location">{t('groups.location')}</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder={t('groups.location_placeholder')}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="maxMembers">{t('groups.max_members')}</label>
              <input
                type="number"
                id="maxMembers"
                name="maxMembers"
                value={formData.maxMembers}
                onChange={handleChange}
                placeholder={t('groups.unlimited')}
                min="0"
              />
              <span className="hint">{t('groups.max_members_hint')}</span>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="schedule">{t('groups.schedule')} ({t('optional')})</label>
              <input
                type="text"
                id="schedule"
                name="schedule"
                value={formData.schedule}
                onChange={handleChange}
                placeholder={t('groups.schedule_placeholder')}
              />
              <span className="hint">{t('groups.schedule_hint')}</span>
            </div>
            
            <div className="form-group">
              <label htmlFor="meetingPoint">{t('groups.meeting_point')} ({t('optional')})</label>
              <input
                type="text"
                id="meetingPoint"
                name="meetingPoint"
                value={formData.meetingPoint}
                onChange={handleChange}
                placeholder={t('groups.meeting_point_placeholder')}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>{t('groups.banner_image')} ({t('optional')})</label>
            <div className={`image-preview ${imagePreview ? 'has-image' : ''}`}
                 style={imagePreview ? { backgroundImage: `url(${imagePreview})` } : {}}
            >
              {!imagePreview && (
                <div className="upload-placeholder">
                  <FaImage size={30} />
                  <p>{t('groups.upload_banner')}</p>
                </div>
              )}
            </div>
            
            <input
              type="file"
              id="bannerImage"
              accept="image/jpeg, image/png"
              style={{ display: 'none' }}
              onChange={handleImageChange}
            />
            
            <div className="image-actions">
              <CustomButton2
                label={imagePreview ? t('groups.change_image') : t('groups.select_image')}
                onClick={(e) => {
                  e.preventDefault(); // Prevent form submission
                  document.getElementById('bannerImage').click();
                }}
                type="button"
                icon={<FaImage />}
              />
              
              {imagePreview && (
                <CustomButton2
                  label={t('groups.remove_image')}
                  onClick={(e) => {
                    e.preventDefault(); // Prevent form submission
                    handleRemoveImage();
                  }}
                  type="button"
                  icon={<FaTrash />}
                />
              )}
              
              <span className="hint">{t('groups.image_requirements')}</span>
            </div>
          </div>
          
          <div className="form-actions">
            <CustomButton2
              label={t('buttons.cancel')}
              onClick={handleBack}
              type="button"
            />
            <CustomButton2
              label={saving ? t('app.saving') : t('buttons.save_changes')}
              onClick={handleUpdateGroup}
              disabled={saving}
              icon={saving ? <Spinner size="small" showText={false} /> : <FaSave />}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default EditGroupPage;
